import { useMemo, useState } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/form/switch';
import { MetricInfoButton } from '@/components/MetricInfoButton';
import type { TrainingLoad } from '@/types';

interface InsightsPulseChartProps {
  data: TrainingLoad[];
  className?: string;
}

interface TooltipPayloadEntry {
  name?: string;
  value?: number;
  color?: string;
}

interface PulseTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
}

/** Custom HUD tooltip with blur background */
function PulseTooltip({ active, payload, label }: PulseTooltipProps) {
  if (!active || !payload?.length) return null;

  return (
    <div className="px-3 py-2.5 rounded-lg border border-white/[0.12] bg-[rgba(12,16,24,0.92)] backdrop-blur-[20px] shadow-lg">
      <p className="text-[10px] font-mono tracking-[0.1em] text-muted-foreground mb-1.5 uppercase">
        {label}
      </p>
      {payload.map((entry: TooltipPayloadEntry, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <div 
            className="w-1.5 h-1.5 rounded-full" 
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-[10px] font-mono text-muted-foreground">{entry.name}:</span>
          <span className="text-xs font-mono tabular-nums text-foreground">
            {typeof entry.value === 'number' ? entry.value.toFixed(1) : (entry.value ?? '—')}
          </span>
        </div>
      ))}
    </div>
  );
}

export function InsightsPulseChart({ data, className }: InsightsPulseChartProps) {
  const [isAdvanced, setIsAdvanced] = useState(true);

  const chartData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    const filtered = data.filter((d) => d?.date);

    return filtered.map((d) => {
      let dateLabel = d.date;
      try {
        dateLabel = format(parseISO(d.date), 'MMM d');
      } catch {
        // keep dateLabel as d.date on parse error
      }
      const ctl = d.ctl ?? 0;
      const atl = d.atl ?? 0;
      const efficiency = atl > 0 ? (ctl / atl) * 100 : undefined;
      return {
        ...d,
        dateLabel,
        fitnessScore: ctl - Math.abs(d.tsb || 0) * 0.3,
        efficiency,
      };
    });
  }, [data]);

  if (chartData.length === 0) {
    return (
      <div className={cn('p-6 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]', className)}>
        <p className="text-xs font-mono text-muted-foreground text-center">NO SIGNAL DATA</p>
      </div>
    );
  }

  return (
    <div className={cn(
      'p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className
    )}>
      {/* Header with controls */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-1 rounded-full bg-[hsl(170,80%,45%)]" />
            <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
              {isAdvanced ? 'TRAINING LOAD ANALYSIS' : 'LOAD VS READINESS — PULSE SIGNAL'}
            </span>
            <MetricInfoButton metricKey="pmc" />
          </div>
          {/* Simple/Advanced toggle */}
          <div className="flex items-center gap-2">
            <span className={cn('text-[9px] font-mono tracking-wider uppercase', !isAdvanced ? 'text-[#00FFC8]' : 'text-muted-foreground')}>
              SIMPLE
            </span>
            <Switch
              checked={isAdvanced}
              onCheckedChange={setIsAdvanced}
              className="h-4 w-7 data-[state=checked]:bg-[hsl(170,80%,45%)]"
            />
            <span className={cn('text-[9px] font-mono tracking-wider uppercase', isAdvanced ? 'text-[#00FFC8]' : 'text-muted-foreground')}>
              ADV
            </span>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-end gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-[2px] rounded bg-[hsl(170,80%,45%)]" />
              <span className="text-[9px] font-mono text-muted-foreground">{isAdvanced ? 'BASE' : 'FITNESS'}</span>
            </div>
            {isAdvanced && (
              <>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-[2px] rounded bg-[hsl(35,85%,55%)]" />
                  <span className="text-[9px] font-mono text-muted-foreground">LOAD</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-[2px] rounded bg-[hsl(200,65%,55%)]" />
                  <span className="text-[9px] font-mono text-muted-foreground">FRESH</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-[2px] rounded bg-[hsl(280,65%,60%)]" />
                  <span className="text-[9px] font-mono text-muted-foreground">BALANCE</span>
                </div>
              </>
            )}
          </div>
        </div>

      {/* Chart */}
      <div className="h-52 sm:h-64 lg:h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="ctlGlow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(170, 80%, 45%)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="hsl(170, 80%, 45%)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="fitnessGlowPulse" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(170, 80%, 45%)" stopOpacity={0.2} />
                <stop offset="95%" stopColor="hsl(170, 80%, 45%)" stopOpacity={0} />
              </linearGradient>
              <filter id="neonGlow">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.04)" vertical={false} />

            <XAxis
              dataKey="dateLabel"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}
              interval="preserveStartEnd"
              minTickGap={40}
            />

            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.25)', fontFamily: 'monospace' }}
              width={35}
            />

            {isAdvanced && (
              <>
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.25)', fontFamily: 'monospace' }}
                  width={35}
                />
                <YAxis
                  yAxisId="efficiency"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.25)', fontFamily: 'monospace' }}
                  width={35}
                  domain={['auto', 'auto']}
                />
              </>
            )}

            <Tooltip content={<PulseTooltip />} />

            {isAdvanced && (
              <ReferenceLine yAxisId="right" y={0} stroke="rgba(255,255,255,0.08)" strokeDasharray="4 4" />
            )}

            {/* Simple mode: single fitness score area */}
            {!isAdvanced && (
              <>
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="fitnessScore"
                  stroke="none"
                  fill="url(#fitnessGlowPulse)"
                  fillOpacity={1}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="fitnessScore"
                  stroke="hsl(170, 80%, 45%)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3, fill: 'hsl(170, 80%, 45%)', stroke: 'none' }}
                  name="Fitness"
                  filter="url(#neonGlow)"
                />
              </>
            )}

            {/* Advanced mode: CTL, ATL, TSB */}
            {isAdvanced && (
              <>
                <Area yAxisId="left" type="monotone" dataKey="ctl" stroke="none" fill="url(#ctlGlow)" fillOpacity={1} />
                <Line
                  yAxisId="left" type="monotone" dataKey="ctl"
                  stroke="hsl(170, 80%, 45%)" strokeWidth={2} dot={false}
                  activeDot={{ r: 3, fill: 'hsl(170, 80%, 45%)', stroke: 'none' }}
                  name="Aerobic Base" filter="url(#neonGlow)"
                />
                <Line
                  yAxisId="left" type="monotone" dataKey="atl"
                  stroke="hsl(35, 85%, 55%)" strokeWidth={1.5} dot={false}
                  activeDot={{ r: 3, fill: 'hsl(35, 85%, 55%)', stroke: 'none' }}
                  name="Recent Load" strokeDasharray="4 2"
                />
                <Line
                  yAxisId="right" type="monotone" dataKey="tsb"
                  stroke="hsl(200, 65%, 55%)" strokeWidth={1.5} dot={false}
                  activeDot={{ r: 3, fill: 'hsl(200, 65%, 55%)', stroke: 'none' }}
                  name="Freshness Index"
                />
                <Line
                  yAxisId="efficiency" type="monotone" dataKey="efficiency"
                  stroke="hsl(280, 65%, 60%)" strokeWidth={1.5} dot={false}
                  activeDot={{ r: 3, fill: 'hsl(280, 65%, 60%)', stroke: 'none' }}
                  name="Load Balance"
                  connectNulls={false}
                />
              </>
            )}

            {isAdvanced && (
              <Brush
                dataKey="dateLabel"
                height={24}
                stroke="rgba(255,255,255,0.08)"
                fill="rgba(12,16,24,0.9)"
                tickFormatter={() => ''}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
